import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../components/core/Button/Button";
import { DoubleStructure } from "../../../components/core/DoubleStructure/DoubleStructure";
import { EmptyState } from "../../../components/EmptyState/EmptyState";
import {
  EMPTY_STATE_COURSES_MESSAGE,
  COURSE_LABEL,
  COURSES_LABEL,
  NAME_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_COURSES_MESSAGE,
  GROUP_TARGETS_LABEL,
  EXECUTION_MODE_LABEL,
  URL_LABEL,
  DELETE_COURSE_LABEL,
  DELETE_COURSE_CONFIRM_MESSAGE,
  INTERNAL_GRADE_IT_LABEL,
  RECIPIENT_LABEL,
  EMPTY_STRING,
  TYPOLOGY_LABEL,
  DURATION_LABEL,
  CATEGORY_LABEL,
} from "../../../constants/Dictionary";
import coursesImage from "../../../images/courses.png";
import { useDebounce } from "use-debounce";
import {
  FilterProps,
  FontWeightEnum,
  AlignmentEnum,
} from "../../../types/AppTypes";
import {
  COURSES_PAGE_SIZE,
  PermissionsEnum,
} from "../../../constants/AppConstants";
import { StyledCourses } from "./Courses.styled";
import { Loader } from "../../../components/core/Loader/Loader";
import SearchInput from "../../../components/core/SearchInput/SearchInput";
import { FiltersPanel } from "../../../components/FiltersPanel/FiltersPanel";
import { BodyTyp } from "../../../components/Typography/Typography";
import { getCoursesTableColumns } from "../../../constants/TableColumns";
import {
  ReactComponent as PlusIcon,
  ReactComponent as AddIcon,
} from "../../../icons/add.svg";
import { Link, LinkSizeEnum } from "../../../components/core/Link/Link";
import { CourseModal } from "./CourseModal/CourseModal";
import { Course } from "../../../types/api/courses.type";
import { PopupConfirm } from "../../../components/PopupConfirm/PopupConfirm";
import { getCoursesFiltersTitles } from "../../../constants/FilterTitles";
import { useAppSelector } from "../../../hooks/store.hook";
import { roleHasPermission } from "../../../utils/roleAuth.utils";
import { FiltersSelected } from "../../../components/FiltersSelected/FiltersSelected";
import { findLabelFromValue } from "../../../utils/orgFunctions.utils";
import {
  TextBoxGrid,
  TextBox,
} from "../../../components/TextBoxGrid/TextBoxGrid.component";
import TargetGroups from "../../../components/TargetGroups/TargetGroups";
import { Table } from "../../../components/core/Table/Table";
import LoadingThumbnail from "../../../components/LoadingThumbnail/LoadingThumbnail";
import { useDispatch } from "react-redux";
import {
  setFiltersCourses,
  setPaginationCourses,
  setSearchCourses,
} from "../../../stores/actions/formation.actions";
import { useDimensions } from "../../../hooks/useDimensions.hook";
import {
  useFormationCourse,
  useFormationCourseFilters,
  useDeleteFormationCourse,
} from "../../../hooks/formationCourse.hook";

type CoursesProps = {
  addLink: (component: React.ReactNode) => void;
};

export const Courses = ({ addLink }: CoursesProps) => {
  const dispatch = useDispatch();
  const { data: filtersData, isLoading: filterLoading } =
    useFormationCourseFilters();

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const { filtersCourses, searchCourses, paginationCourses } = useAppSelector(
    (state) => state.formation.courses,
  );
  const [debounceSearchCourse] = useDebounce(searchCourses, 500);
  const [openCourseModal, setOpenCourseModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Course | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const joinFilters = Object.values(filtersData ?? []).flat();

  const { isMobile } = useDimensions();

  const { data, isLoading, isFetching } = useFormationCourse({
    filtersCourses,
    paginationCourses,
    debounceSearchCourse,
  });
  const { mutateAsync: deleteCourseApi, isPending: isDeleting } =
    useDeleteFormationCourse();

  const filtersAreActive = filtersCourses.length > 0;

  const handleFilters = (filters: FilterProps[]) => {
    dispatch(setPaginationCourses(0));
    dispatch(setFiltersCourses(filters));
  };

  const onEdit = (record: Course) => {
    setSelectedRecord(record);
    setOpenCourseModal(true);
  };

  const onDelete = (record: Course) => {
    setSelectedRecord(record);
    setOpenDeleteModal(true);
  };

  const deleteCourse = async (id: number) => {
    await deleteCourseApi(id);

    setOpenDeleteModal(false);
    setSelectedRecord(null);
  };

  useEffect(() => {
    if (!isLoading && data && hasData === null) {
      if (filtersAreActive || searchCourses.length > 0) {
        setHasData(true);
        return;
      }

      const newHasData = data.pagination.total > 0;
      setHasData(newHasData);

      if (newHasData) {
        addLink(
          <Link
            onClick={() => {
              setSelectedRecord(null);
              setOpenCourseModal(true);
            }}
            icon={<PlusIcon />}
            size={LinkSizeEnum.SMALL}
          >
            {COURSE_LABEL}
          </Link>,
        );
      }
    }
  }, [data, hasData, isLoading]);

  useEffect(() => {
    return () => {
      addLink(null);
    };
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPaginationCourses(0));
  }, [debounceSearchCourse]);

  const expandedRow = (record: Course): React.ReactElement | null => {
    return (
      <>
        {isMobile && (
          <TextBoxGrid
            numCol={2}
            items={[
              { label: DURATION_LABEL, text: record.duration },
              {
                label: TYPOLOGY_LABEL,
                text: findLabelFromValue(
                  orgSettings.courses.typologies,
                  record.typology,
                ),
              },
              {
                label: CATEGORY_LABEL,
                text: findLabelFromValue(
                  orgSettings.courses.categories,
                  record.category,
                ),
              },
            ]}
          />
        )}
        {!record.adHocCollaborator && (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: GROUP_TARGETS_LABEL,
                text: <TargetGroups cc={record.cc} />,
              },
              {
                label: INTERNAL_GRADE_IT_LABEL,
                text:
                  record.internalGrade && record.internalGrade.length > 0
                    ? record.internalGrade.join(", ")
                    : EMPTY_STRING,
              },
            ]}
          />
        )}
        {record.adHocCollaborator ? (
          <TextBoxGrid
            numCol={isMobile ? 2 : 3}
            items={[
              {
                label: RECIPIENT_LABEL,
                text: record.collaborator
                  ? `${record.collaborator?.surname} ${record.collaborator?.name}`
                  : EMPTY_STRING,
              },
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.courses.executionModes,
                  record.executionMode,
                ),
              },
              ...(!isMobile ? [{ label: URL_LABEL, text: record.url }] : []),
            ]}
          />
        ) : (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.courses.executionModes,
                  record.executionMode,
                ),
              },
              ...(!isMobile ? [{ label: URL_LABEL, text: record.url }] : []),
            ]}
          />
        )}
        {isMobile && <TextBox label={URL_LABEL} text={record.url} />}
        <TextBox label={DESCRIPTION_LABEL} text={record.description} />
      </>
    );
  };

  const calcTableHeight = (): string => {
    let defaultHeight = 360;

    if (filtersAreActive) {
      defaultHeight += 52;
    }

    return `calc(100dvh - ${defaultHeight}px)`;
  };

  return (
    <>
      <StyledCourses>
        {isLoading && hasData === null ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : null}
        {!isLoading && hasData === false ? (
          <div className="empty-content">
            <EmptyState
              label={EMPTY_STATE_COURSES_MESSAGE}
              imageSrc={coursesImage}
            >
              {roleHasPermission(
                profile,
                PermissionsEnum.EDIT_TECH_FORMATION,
              ) && (
                <Button
                  label={COURSE_LABEL}
                  variant={ButtonVariants.SECONDARY}
                  size={ButtonSizes.REGULAR}
                  icon={<AddIcon />}
                  onClick={() => setOpenCourseModal(true)}
                />
              )}
            </EmptyState>
          </div>
        ) : null}
        {hasData ? (
          <>
            <div className="courses-header-container">
              <DoubleStructure
                verticalAlignment={AlignmentEnum.CENTER}
                variant="primary"
              >
                <div>
                  {data != null && (
                    <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                      {`${data.pagination.total} ${COURSES_LABEL}`}
                    </BodyTyp>
                  )}
                </div>
                {(data || searchCourses.length > 0) && (
                  <div className="filters-container">
                    <div className="search-container">
                      <SearchInput
                        value={searchCourses}
                        onChange={(value) => dispatch(setSearchCourses(value))}
                        label={NAME_LABEL}
                        name={"search"}
                      />
                    </div>
                    <FiltersPanel
                      data={filtersData}
                      setSelectedFilters={handleFilters}
                      selectedFilters={filtersCourses}
                      filtersTitles={getCoursesFiltersTitles()}
                      loading={filterLoading}
                    />
                  </div>
                )}
              </DoubleStructure>
              <FiltersSelected
                data={joinFilters}
                filters={filtersCourses}
                setFilters={handleFilters}
              />
            </div>
            <div className="courses-content">
              <Table<Course>
                loading={isFetching}
                headers={getCoursesTableColumns(orgSettings, isMobile)}
                scrollY={calcTableHeight()}
                data={data?.data ?? []}
                {...(roleHasPermission(
                  profile,
                  PermissionsEnum.EDIT_TECH_FORMATION,
                ) && {
                  onEdit: onEdit,
                  onDelete: onDelete,
                })}
                rowKey={"id"}
                pagination={{
                  current: paginationCourses + 1,
                  onChange: (pageNumber) =>
                    dispatch(setPaginationCourses(pageNumber - 1)),
                  total: data?.pagination.total ?? 0,
                  pageSize: COURSES_PAGE_SIZE,
                }}
                expandable={{
                  expandedRowRender: expandedRow,
                }}
                emptyText={EMPTY_COURSES_MESSAGE}
              />
            </div>
          </>
        ) : null}
      </StyledCourses>
      {openCourseModal && (
        <CourseModal
          isOpen={openCourseModal}
          closeModal={() => {
            setSelectedRecord(null);
            setOpenCourseModal(false);
          }}
          {...(selectedRecord && {
            record: selectedRecord,
          })}
        />
      )}
      <PopupConfirm
        isOpen={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={
          selectedRecord ? () => deleteCourse(selectedRecord?.id) : () => {}
        }
        label={DELETE_COURSE_LABEL}
        popupText={
          selectedRecord
            ? `${DELETE_COURSE_CONFIRM_MESSAGE} ${selectedRecord.name}?`
            : "error"
        }
        isLoading={isDeleting}
      />
    </>
  );
};
